import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';

const blankSlab = () => ({
    _localId: `new-${Math.random().toString(36).slice(2, 9)}`,
    slab_id: null,
    min_amount: '',
    max_amount: '',
    fee_amount: '',
});

const EditConvenienceFee = ({
    show,
    handleClose,
    feeId = 1,
    currentFeeType = 'PERCENTAGE',
    currentDefaultPercentage = null,
    currentSlabs = [],
    onUpdateSuccess,
}) => {
    const [feeType, setFeeType] = useState(currentFeeType);
    const [defaultPercentage, setDefaultPercentage] = useState(currentDefaultPercentage ?? '');
    const [slabs, setSlabs] = useState([]);
    const [originalSlabs, setOriginalSlabs] = useState([]);
    const [removedSlabIds, setRemovedSlabIds] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!show) return;
        setFeeType(currentFeeType || 'PERCENTAGE');
        setDefaultPercentage(currentDefaultPercentage ?? '');
        const mapped = (currentSlabs || []).map(s => ({
            _localId: `srv-${s.slab_id}`,
            slab_id: s.slab_id,
            min_amount: s.min_amount ?? '',
            max_amount: s.max_amount == null ? '' : s.max_amount,
            fee_amount: s.fee_amount ?? '',
        }));
        setSlabs(mapped.length ? mapped : [blankSlab()]);
        setOriginalSlabs(mapped.map(s => ({ ...s })));
        setRemovedSlabIds([]);
    }, [show, currentFeeType, currentDefaultPercentage, currentSlabs]);

    const updateSlab = (localId, field, value) => {
        setSlabs(prev => prev.map(s => s._localId === localId ? { ...s, [field]: value } : s));
    };

    const addSlab = () => setSlabs(prev => [...prev, blankSlab()]);

    const removeSlab = (localId) => {
        const target = slabs.find(s => s._localId === localId);
        if (!target) return;
        if (target.slab_id) setRemovedSlabIds(prev => [...prev, target.slab_id]);
        setSlabs(prev => prev.filter(s => s._localId !== localId));
    };

    const validate = () => {
        if (feeType === 'PERCENTAGE') {
            const pct = parseFloat(defaultPercentage);
            if (Number.isNaN(pct) || pct <= 0 || pct > 100) {
                toast.error('Default percentage must be between 0 and 100');
                return false;
            }
        } else {
            if (slabs.length === 0) {
                toast.error('Add at least one slab');
                return false;
            }
            for (let i = 0; i < slabs.length; i++) {
                const s = slabs[i];
                const min = parseFloat(s.min_amount);
                const fee = parseFloat(s.fee_amount);
                const max = s.max_amount === '' || s.max_amount == null ? null : parseFloat(s.max_amount);
                if (Number.isNaN(min) || min <= 0) {
                    toast.error(`Slab #${i + 1}: minimum amount must be > 0`);
                    return false;
                }
                if (Number.isNaN(fee) || fee <= 0) {
                    toast.error(`Slab #${i + 1}: fee must be > 0`);
                    return false;
                }
                if (max !== null && max <= min) {
                    toast.error(`Slab #${i + 1}: max must be greater than min, or empty for open-ended`);
                    return false;
                }
            }
        }
        return true;
    };

    const slabDiffers = (current, original) => (
        parseFloat(current.min_amount) !== parseFloat(original.min_amount) ||
        (current.max_amount === '' ? null : parseFloat(current.max_amount)) !==
            (original.max_amount === '' ? null : parseFloat(original.max_amount)) ||
        parseFloat(current.fee_amount) !== parseFloat(original.fee_amount)
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setIsSubmitting(true);
        try {
            const updatePayload = {
                fee_id: feeId,
                modified_by: 'admin',
                fee_type: feeType,
            };
            if (feeType === 'PERCENTAGE') {
                updatePayload.default_percentage = parseFloat(defaultPercentage);
            }
            const updateRes = await _axios.put(`${ServerPath}/convenience-fee/update`, updatePayload);
            if (updateRes.data?.success !== 1) {
                toast.error('Failed to update convenience fee');
                setIsSubmitting(false);
                return;
            }

            if (feeType === 'FIXED_SLAB') {
                for (const slabId of removedSlabIds) {
                    try {
                        await _axios.delete(`${ServerPath}/convenience-fee/slabs/${slabId}`);
                    } catch (err) { console.error('Slab delete error:', err); }
                }
                const origMap = new Map(originalSlabs.map(s => [s.slab_id, s]));
                for (let i = 0; i < slabs.length; i++) {
                    const s = slabs[i];
                    const max = s.max_amount === '' || s.max_amount == null ? null : parseFloat(s.max_amount);
                    const body = {
                        min_amount: parseFloat(s.min_amount),
                        max_amount: max,
                        fee_amount: parseFloat(s.fee_amount),
                        sort_order: i + 1,
                    };
                    try {
                        if (!s.slab_id) {
                            await _axios.post(`${ServerPath}/convenience-fee/slabs`, { fee_id: feeId, ...body });
                        } else {
                            const orig = origMap.get(s.slab_id);
                            if (!orig || slabDiffers(s, orig) || (orig && i + 1 !== originalSlabs.indexOf(orig) + 1)) {
                                await _axios.patch(`${ServerPath}/convenience-fee/slabs/${s.slab_id}`, body);
                            }
                        }
                    } catch (err) {
                        console.error('Slab save error:', err);
                    }
                }
            }

            toast.success('Convenience fee updated successfully');
            if (onUpdateSuccess) await onUpdateSuccess();
            handleClose();
        } catch (err) {
            console.error('Update convenience fee error:', err);
            toast.error('Failed to update convenience fee');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton style={{ border: 'none' }}>
                <Modal.Title style={{ fontSize: '1rem', fontWeight: 500, color: '#111827' }}>
                    Edit Convenience Fee Management
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ paddingTop: '0.5rem' }}>
                <Form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                        <Col md={5}>
                            <Form.Label className="text-secondary fw-semibold">
                                Convenience Fee Type <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex flex-column gap-2 mt-1">
                                <Form.Check
                                    type="radio"
                                    label="Percentage"
                                    name="edit-fee-type"
                                    checked={feeType === 'PERCENTAGE'}
                                    onChange={() => setFeeType('PERCENTAGE')}
                                />
                                <Form.Check
                                    type="radio"
                                    label="Fixed Amount Slab"
                                    name="edit-fee-type"
                                    checked={feeType === 'FIXED_SLAB'}
                                    onChange={() => setFeeType('FIXED_SLAB')}
                                />
                            </div>
                        </Col>
                        <Col md={7}>
                            {feeType === 'PERCENTAGE' ? (
                                <Form.Group>
                                    <Form.Label className="text-secondary">
                                        Default Percentage <span className="text-danger">*</span>
                                    </Form.Label>
                                    <div style={{
                                        display: 'flex', alignItems: 'center',
                                        border: '1px solid #D1D5DB', borderRadius: '0.375rem',
                                        padding: '0.5rem 0.75rem', backgroundColor: 'white'
                                    }}>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={defaultPercentage}
                                            onChange={(e) => setDefaultPercentage(e.target.value)}
                                            style={{ border: 'none', background: 'transparent', width: '100%', outline: 'none', padding: 0 }}
                                        />
                                        <span style={{ color: '#4B5563', marginLeft: 4 }}>%</span>
                                    </div>
                                </Form.Group>
                            ) : (
                                <div>
                                    <Form.Label className="text-secondary fw-semibold mb-2">
                                        Slab Configuration <span className="text-danger">*</span>
                                    </Form.Label>
                                    <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                                        <Row className="small fw-semibold text-secondary mb-1 g-2">
                                            <Col xs={3}>Min Amount</Col>
                                            <Col xs={3}>Max Amount</Col>
                                            <Col xs={3}>Fee</Col>
                                            <Col xs={3}></Col>
                                        </Row>
                                        {slabs.map((s, idx) => (
                                            <Row key={s._localId} className="g-2 mb-2 align-items-center">
                                                <Col xs={3}>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        size="sm"
                                                        value={s.min_amount}
                                                        onChange={(e) => updateSlab(s._localId, 'min_amount', e.target.value)}
                                                    />
                                                </Col>
                                                <Col xs={3}>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        size="sm"
                                                        placeholder="Open"
                                                        value={s.max_amount}
                                                        onChange={(e) => updateSlab(s._localId, 'max_amount', e.target.value)}
                                                    />
                                                </Col>
                                                <Col xs={3}>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        size="sm"
                                                        value={s.fee_amount}
                                                        onChange={(e) => updateSlab(s._localId, 'fee_amount', e.target.value)}
                                                    />
                                                </Col>
                                                <Col xs={3}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeSlab(s._localId)}
                                                        disabled={slabs.length === 1}
                                                    >×</button>
                                                </Col>
                                            </Row>
                                        ))}
                                    </div>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={addSlab}
                                    >
                                        [ + Add Slab ]
                                    </Button>
                                </div>
                            )}
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end" style={{ marginTop: '1.5rem' }}>
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                backgroundColor: '#6366F1', border: 'none',
                                padding: '0.5rem 2rem', borderRadius: '0.375rem', fontSize: '0.875rem'
                            }}
                        >
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default EditConvenienceFee;
